import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Typeahead, Token} from 'react-bootstrap-typeahead';
import {capitalize} from 'lodash';
import {FormGroup, ControlLabel, HelpBlock, InputGroup} from 'react-bootstrap';

import 'react-bootstrap-typeahead/css/Typeahead.css';

import './styles.scss';

const MultiSelect = (props) => {
  const {
    label,
    placeholder,
    handleChange,
    type,
    error,
    labelKey,
    selected,
    selectAll,
    multiple,
    nullPrefix,
    hasChildren,
    filterByKeys = [],
    renderCustomCategoryToken,
    keepFocusAfterSelect,
    renderMenu,
    handleFocus,
    handleBlur,
    isFetching,
    clearOnSelect,
    elemsBeforeTypeahead,
    elemsAfterTypehead,
    typeAheadProps,
    renderElemsInsideInputGroup,
    selectHintOnEnter,
    clearButton,
    renderToken,
    renderMenuItemChildren,
    renderInput,
  } = props;

  const [inputText, setInputText] = useState('');
  const typeaheadRef = useRef(null);

  const printErrors = (error) => {
    return error && error.messages.map((each) => {
      return (
        <HelpBlock
          key={`multiselect-error-${each.text}`}
          className={'text-danger error'}
        >
          {each.text}
        </HelpBlock>
      );
    });
  };

  /**
   * Append the nestLevel for each node so that a list can be properly indented
   *
   * @param {Array} list contains a childid and a parentid to be parsed for depth
   * @param {String} childId is the name of the key for the childid in the list
   * @param {String} parentId is the name of the key for the parentid in the list
   * @returns {Array} [...list, nestLevel]
   */
  const appendDepth = (list, childId, parentId) => {
    const dictionary = new Map();

    list.forEach((item) => {
      dictionary.set(item[childId], {...item, depth: 0});
    });

    list.forEach((item) => {
      let parentid = item[parentId];
      let depth = 0;

      while (parentid) {
        parentid = (dictionary.get(parentid) || {})[parentId];
        depth++;

        // Escape
        if (depth > 10) {
          parentid = 0;
        }
      }

      dictionary.set(item[childId], {...item, depth});
    });

    // Update list
    return list.map((item) => {
      return {...item, nestLevel: dictionary.get(item[childId])['depth']};
    });
  };

  let {options} = props;

  const typeAheadPropsInternal = {...typeAheadProps};

  const nullOption = (nullPrefix === '')
    ? {[labelKey]: capitalize(`All ${label}`), id: null}
    : {[labelKey]: capitalize(nullPrefix), id: null};

  if (renderToken) {
    typeAheadPropsInternal.renderToken = renderToken;
  }

  if (renderInput) {
    typeAheadPropsInternal.renderInput = renderInput;
  }

  if (renderMenuItemChildren) {
    typeAheadPropsInternal.renderMenuItemChildren = renderMenuItemChildren;
  }

  if (renderCustomCategoryToken) {
    typeAheadPropsInternal.renderToken = (option, props) => (
      <Token key={option.id} title={option.keywords} onRemove={props.onRemove}>
        {option[labelKey]}
      </Token>
    );
  }

  if (filterByKeys.length) {
    typeAheadPropsInternal.filterBy = [labelKey, ...filterByKeys];
  }

  // Append nestLevel to indent options
  switch (labelKey) {
    case 'plantypename':
      options = appendDepth(options, 'id', 'parentid');
  }

  if (labelKey === 'categoryname' || hasChildren) {
    typeAheadPropsInternal.filterBy = [labelKey, 'parentname', ...filterByKeys];
  }

  if (typeof renderMenu == 'function') {
    typeAheadPropsInternal.renderMenu = renderMenu;
  }

  if (typeof handleBlur == 'function') {
    typeAheadPropsInternal.onBlur = () => {
      const instance = typeaheadRef.current.getInstance();
      handleBlur(instance);
    };
  }

  if (typeof handleFocus == 'function') {
    typeAheadPropsInternal.onFocus = () => {
      const instance = typeaheadRef.current.getInstance();
      handleFocus(instance);
    };
  }

  if (keepFocusAfterSelect) {
    typeAheadPropsInternal.onInputChange = (text) => {
      setInputText(text);
    };
  }

  let selectOptions = options;
  if (selectAll) {
    selectOptions = [nullOption, ...options];
  }

  if (options.length < 1) {
    typeAheadPropsInternal.disabled = true;
  }

  typeAheadPropsInternal.selectHintOnEnter = selectHintOnEnter;

  const noOptionsPlaceholder = isFetching ? 'Loading...' : 'No options available.';

  const typeAhead = (
    <Typeahead
      id={`multiselect-${label}`}
      ref={typeaheadRef}
      multiple={multiple}
      clearButton={clearButton}
      options={selectOptions}
      selected={selected === null ? [nullOption] : selected}
      placeholder={options.length > 0 ? placeholder : noOptionsPlaceholder}
      onChange={(selected) => {
        handleChange(selected, type);

        if (!typeaheadRef.current) {
          return;
        }

        if (clearOnSelect) {
          typeaheadRef.current.getInstance().clear();
          typeaheadRef.current.getInstance()._input.value = inputText;
        }
      }}
      {...typeAheadPropsInternal}
    />
  );

  return (
    <FormGroup data-component={'MultiSelect'}>
      {label && <ControlLabel>{label}</ControlLabel>}
      {renderElemsInsideInputGroup ? (
        <InputGroup>
          {elemsBeforeTypeahead}
          {typeAhead}
          {elemsAfterTypehead}
        </InputGroup>
      ) : (
        <>
          {elemsBeforeTypeahead}
          {typeAhead}
          {elemsAfterTypehead}
        </>
      )}
      {printErrors(error)}
    </FormGroup>
  );
};

MultiSelect.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  labelKey: PropTypes.string,
  selected: PropTypes.array,
  selectAll: PropTypes.bool,
  multiple: PropTypes.bool,
  nullPrefix: PropTypes.string,
  hasChildren: PropTypes.bool,
  filterByKeys: PropTypes.array,
  renderCustomCategoryToken: PropTypes.bool,
  keepFocusAfterSelect: PropTypes.bool,
  renderMenu: PropTypes.func,
  handleFocus: PropTypes.func,
  handleBlur: PropTypes.func,
  isFetching: PropTypes.bool,
  clearOnSelect: PropTypes.bool,
  elemsBeforeTypeahead: PropTypes.node,
  elemsAfterTypehead: PropTypes.node,
  typeAheadProps: PropTypes.object,
  renderElemsInsideInputGroup: PropTypes.bool,
  selectHintOnEnter: PropTypes.bool,
  clearButton: PropTypes.bool,
  renderToken: PropTypes.func,
  renderMenuItemChildren: PropTypes.func,
  renderInput: PropTypes.func,
  options: PropTypes.array,
};

MultiSelect.defaultProps = {
  labelKey: 'label',
  error: false,
  multiple: true,
  selectAll: false,
  nullPrefix: '',
  hasChildren: false,
  filterByKeys: [],
  renderCustomCategoryToken: false,
  keepFocusAfterSelect: false,
  isFetching: false,
  clearOnSelect: false,
  typeAheadProps: {},
  renderElemsInsideInputGroup: false,
  selectHintOnEnter: false,
  clearButton: false,
  options: [],
};

export default MultiSelect;
